import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Users, Calendar, CalendarPlus, ArrowLeft, Award, Target, Shield, MapPin, UserCircle, BarChart3, ChevronRight, Download } from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CNVT2027Tab from '../components/CNVT2027Tab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Linkify = ({ children }) => {
  if (typeof children !== 'string') return children;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
  const parts = children.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      const href = part.startsWith('http') ? part : `https://${part}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">{part}</a>;
    }
    return part;
  });
};

const stageLabels = {
  pool_a: 'Pool A', pool_b: 'Pool B', pool_c: 'Pool C', pool_d: 'Pool D',
  quarterfinal: 'Quarter-Finals', semifinal: 'Semi-Finals', gala: 'Gala Match - Above 45 Years', final: 'Final', group: 'Group Stage'
};

const PoolTable = ({ pool, teams, teamMap, forfeitTeams }) => (
  <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
      <h3 className="font-heading text-sm font-bold text-white uppercase">Pool {pool}</h3>
      <span className="text-[10px] text-gray-600 uppercase tracking-wider">Tiebreaker: GD → GF → GA</span>
    </div>
    <Table>
      <TableHeader>
        <TableRow className="border-white/10 hover:bg-transparent">
          <TableHead className="text-gray-400 text-xs w-8">#</TableHead>
          <TableHead className="text-gray-400 text-xs">Team</TableHead>
          <TableHead className="text-gray-400 text-xs text-center">P</TableHead>
          <TableHead className="text-gray-400 text-xs text-center">W</TableHead>
          <TableHead className="text-gray-400 text-xs text-center">D</TableHead>
          <TableHead className="text-gray-400 text-xs text-center">L</TableHead>
          <TableHead className="text-gray-400 text-xs text-center bg-amber-500/8 border-x border-amber-500/10">GF</TableHead>
          <TableHead className="text-gray-400 text-xs text-center bg-amber-500/5 border-r border-amber-500/10">GA</TableHead>
          <TableHead className="text-gray-400 text-xs text-center bg-amber-500/10 border-r border-amber-500/10">GD</TableHead>
          <TableHead className="text-gray-400 text-xs text-center text-emerald-500">PTS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {teams.map((team, i) => {
          const td = teamMap?.[team.team_id];
          const logo = td?.logo_data || td?.logo_url || td?.logo;
          const gd = team.goals_for - team.goals_against;
          return (
            <TableRow key={team.team_id} className={`border-white/5 ${i < 2 ? 'bg-emerald-500/5' : ''}`}>
              <TableCell><span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 2 ? 'bg-emerald-500 text-white' : 'bg-white/5 text-gray-500'}`}>{i+1}</span></TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {logo ? <img src={logo} alt="" className="w-6 h-6 object-contain rounded" /> : <div className="w-6 h-6 bg-white/5 rounded" />}
                  <span className="text-white text-sm font-medium">{team.name}{forfeitTeams?.has(team.team_id) ? '*' : ''}</span>
                </div>
              </TableCell>
              <TableCell className="text-center text-gray-300 text-sm">{team.played}</TableCell>
              <TableCell className="text-center text-emerald-500 text-sm">{team.won}</TableCell>
              <TableCell className="text-center text-gray-400 text-sm">{team.drawn}</TableCell>
              <TableCell className="text-center text-red-500 text-sm">{team.lost}</TableCell>
              <TableCell className="text-center text-gray-300 text-sm bg-amber-500/[0.03]">{team.goals_for}</TableCell>
              <TableCell className="text-center text-gray-300 text-sm bg-amber-500/[0.02]">{team.goals_against}</TableCell>
              <TableCell className={`text-center text-sm font-medium bg-amber-500/[0.04] ${gd > 0 ? 'text-emerald-500' : gd < 0 ? 'text-red-500' : 'text-gray-400'}`}>{gd > 0 ? '+' : ''}{gd}</TableCell>
              <TableCell className="text-center text-emerald-500 font-bold">{team.points}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export default function PublicTournamentPage() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [newComment, setNewComment] = useState({ author_name: '', content: '' });
  const [subForm, setSubForm] = useState({ email: '', phone: '', follow_teams: [] });
  const [subPrefs, setSubPrefs] = useState({ notify_match_schedule: true, notify_match_changes: true, notify_referee_assigned: true, notify_field_time_change: true });
  const [subscribing, setSubscribing] = useState(false);
  const [gallerySegments, setGallerySegments] = useState([]);
  const [awards, setAwards] = useState({});

  useEffect(() => { fetchTournamentData(); }, [subdomain]);

  const fetchTournamentData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizations/${subdomain}/public`);
      if (!response.ok) throw new Error(response.status === 404 ? 'Tournament not found' : 'Failed to load');
      const d = await response.json();
      setData(d);
      // Fetch blog
      if (d.organization?.org_id) {
        fetch(`${API_URL}/api/blog/${d.organization.org_id}`).then(r => r.json()).then(setBlogPosts).catch(() => {});
        fetch(`${API_URL}/api/gallery/${d.organization.org_id}`).then(r => r.json()).then(setGallerySegments).catch(() => {});
        fetch(`${API_URL}/api/awards/${d.organization.org_id}`).then(r => r.json()).then(setAwards).catch(() => {});
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const fetchComments = async (postId) => {
    if (!data?.organization) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/${data.organization.org_id}/${postId}/comments`);
      if (res.ok) setPostComments(await res.json());
    } catch { }
  };

  const handleComment = async (postId) => {
    if (!newComment.author_name.trim() || !newComment.content.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/${postId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newComment)
      });
      if (res.ok) {
        setNewComment({ author_name: '', content: '' });
        fetchComments(postId);
      }
    } catch { }
  };

  const fetchGallery = async () => {
    if (!data?.organization) return;
    try {
      const res = await fetch(`${API_URL}/api/gallery/${data.organization.org_id}`);
      if (res.ok) setGallerySegments(await res.json());
    } catch {}
  };

  const handleSubscribe = async () => {
    if (!subForm.email && !subForm.phone) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...subForm, ...subPrefs, org_id: data.organization.org_id })
      });
      const result = await res.json();
      if (res.ok) {
        alert(result.message || 'Subscribed!');
        setSubForm({ email: '', phone: '', follow_teams: [] });
      }
    } catch { }
    finally { setSubscribing(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
      <div className="text-center">
        <Trophy className="mx-auto text-gray-600 mb-4" size={64} />
        <h1 className="text-2xl text-white font-bold mb-2">{error}</h1>
        <Link to="/" className="text-emerald-500 hover:text-emerald-400 flex items-center justify-center gap-2">
          <ArrowLeft size={18} /> Back to Home
        </Link>
      </div>
    </div>
  );

  const { organization, matches, teams, players, sponsors, pool_standings, tournament_info, admin_card_events } = data;
  const teamMap = (teams || []).reduce((m, t) => { m[t.team_id] = t; return m; }, {});
  const getTeamPlayers = (teamId) => (players || []).filter(p => p.team_id === teamId);

  // Compute teams involved in forfeits (for asterisk display)
  const forfeitTeams = new Set();
  (matches || []).forEach(m => {
    if (m.forfeit) {
      forfeitTeams.add(m.home_team_id);
      forfeitTeams.add(m.away_team_id);
    }
  });

  const stageOrder = ['pool_a','pool_b','pool_c','pool_d','quarterfinal','semifinal','gala','final'];
  const groupedMatches = {};
  (matches || []).forEach(m => {
    const s = m.stage || 'group';
    if (!groupedMatches[s]) groupedMatches[s] = [];
    groupedMatches[s].push(m);
  });
  // Sort matches within each stage by date, time, then field (for knockout label alignment)
  Object.keys(groupedMatches).forEach(stage => {
    groupedMatches[stage].sort((a, b) => {
      if ((a.match_date || '') !== (b.match_date || '')) return (a.match_date || '').localeCompare(b.match_date || '');
      if ((a.match_time || '') !== (b.match_time || '')) return (a.match_time || '').localeCompare(b.match_time || '');
      return (a.field || '').localeCompare(b.field || '');
    });
  });

  // Calculate top scorers for player stats
  const playersWithTeams = (players || []).map(p => ({...p, team_name: teamMap[p.team_id]?.name || 'Unknown'}));
  const topScorers = [...playersWithTeams].sort((a,b) => (b.goals||0) - (a.goals||0)).slice(0, 10);
  const topAssists = [...playersWithTeams].sort((a,b) => (b.assists||0) - (a.assists||0)).slice(0, 10);

  // Games played per team
  const completedMatches = (matches || []).filter(m => m.status === 'completed');
  const teamGames = {};
  completedMatches.forEach(m => {
    teamGames[m.home_team_id] = (teamGames[m.home_team_id] || 0) + 1;
    teamGames[m.away_team_id] = (teamGames[m.away_team_id] || 0) + 1;
  });

  const sponsorsByTier = sponsors ? {
    gold: sponsors.filter(s => s.tier === 'gold'),
    silver: sponsors.filter(s => s.tier === 'silver'),
    bronze: sponsors.filter(s => s.tier === 'bronze')
  } : { gold: [], silver: [], bronze: [] };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Header with CNVT Logo */}
      <header className="border-b border-white/10 bg-[#0f0f10]/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/cnvt-logo.jpg" alt="CNVT" className="w-14 h-14 rounded-lg object-contain" />
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-white uppercase">{organization.tournament_name || organization.name}</h1>
                <p className="text-gray-500 text-xs">{organization.name}</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <span className="text-[10px] text-gray-500 border border-white/10 rounded-full px-3 py-1">
                Powered by <Link to="/" className="text-emerald-400 hover:text-emerald-300 font-medium">ScoreCopilot</Link> — The #1 Tournament Platform
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="cnvt2027" className="w-full">
          <TabsList className="bg-[#18181b] border border-white/10 mb-6 flex-wrap">
            <TabsTrigger value="cnvt2027" className="data-[state=active]:bg-red-500 data-[state=active]:text-white font-bold">CNVT 2027</TabsTrigger>
            <TabsTrigger value="standings" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">2026 Standings</TabsTrigger>
            <TabsTrigger value="matches" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">2026 Matches</TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">Teams</TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">Player Stats</TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">Blog</TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">Gallery</TabsTrigger>
            <TabsTrigger value="subscribe" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Notifications</TabsTrigger>
          </TabsList>

          {/* CNVT 2027 */}
          <TabsContent value="cnvt2027">
            <CNVT2027Tab teamMap={teamMap} orgId={organization?.org_id} />
          </TabsContent>

          {/* STANDINGS */}
          <TabsContent value="standings">
            {pool_standings && Object.keys(pool_standings).length > 0 ? (
              <>
                <div className="flex justify-end mb-4">
                  <a href={`${API_URL}/api/export/standings/${organization.org_id}`} download className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 rounded text-sm transition-colors" data-testid="export-standings-csv">
                    <BarChart3 className="w-4 h-4" /> Export CSV
                  </a>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(pool_standings).map(([pool, poolTeams]) => (
                    <PoolTable key={pool} pool={pool} teams={poolTeams} teamMap={teamMap} forfeitTeams={forfeitTeams} />
                  ))}
                </div>

                {/* Knockout Bracket */}
                {(() => {
                  const ko = (matches || []).filter(m => ['quarterfinal','semifinal','final'].includes(m.stage));
                  const qf = ko.filter(m => m.stage === 'quarterfinal').sort((a,b) => (a.field||'').localeCompare(b.field||''));
                  const sf = ko.filter(m => m.stage === 'semifinal').sort((a,b) => (a.field||'').localeCompare(b.field||''));
                  const fin = ko.find(m => m.stage === 'final');
                  if (qf.length === 0) return null;
                  const tn = (id) => {
                    if (!id || id === 'tbd') return 'TBD';
                    const name = teamMap[id]?.name || id;
                    return forfeitTeams.has(id) ? `${name}*` : name;
                  };
                  const tlogo = (id) => {
                    if (!id || id === 'tbd') return null;
                    const t = teamMap[id];
                    return t?.logo_data || t?.logo_url || t?.logo || null;
                  };
                  const TeamSlot = ({ id, score, match }) => {
                    const logo = tlogo(id);
                    const isCompleted = match?.status === 'completed';
                    return (
                      <div className="flex items-center gap-1.5">
                        {logo ? <img src={logo} alt="" className="w-4 h-4 object-contain rounded shrink-0" /> : <div className="w-4 h-4 rounded bg-white/5 shrink-0" />}
                        <span className="text-white text-xs truncate flex-1">{tn(id)}</span>
                        {isCompleted && <span className="text-emerald-400 font-mono text-xs ml-1">{score}</span>}
                      </div>
                    );
                  };
                  const PenLabel = ({ match }) => {
                    if (!match?.penalties) return null;
                    return <div className="text-center text-amber-400 text-[8px] font-bold">(Pen: {match.home_pen_score}-{match.away_pen_score})</div>;
                  };
                  const qfLabels = ['Q1','Q2','Q3','Q4'];
                  const qfDescs = ['A1 v C2','A2 v C1','B1 v D2','B2 v D1'];
                  return (
                    <div className="mt-8">
                      <h3 className="text-white font-heading text-lg font-bold uppercase mb-4">Knockout Bracket</h3>
                      <div className="overflow-x-auto pb-4">
                        <div className="flex items-start gap-4 min-w-[800px]">
                          <div className="flex flex-col gap-4 w-[200px] shrink-0">
                            <h4 className="text-gray-500 text-xs font-bold uppercase text-center">Quarter-Finals</h4>
                            {qf.map((m,i) => (
                              <div key={i} className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                                <div className="px-3 py-1 text-[9px] text-gray-500 uppercase border-b border-white/5">{qfLabels[i]}: {qfDescs[i]}</div>
                                <div className="p-2 space-y-1">
                                  <TeamSlot id={m.home_team_id} score={m.home_score} match={m} />
                                  <TeamSlot id={m.away_team_id} score={m.away_score} match={m} />
                                  <PenLabel match={m} />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center pt-16 shrink-0"><ChevronRight size={20} className="text-emerald-500" /></div>
                          <div className="flex flex-col gap-4 w-[200px] shrink-0 justify-center pt-8">
                            <h4 className="text-gray-500 text-xs font-bold uppercase text-center">Semi-Finals</h4>
                            {(sf.length > 0 ? sf : [{home_team_id:'tbd',away_team_id:'tbd'},{home_team_id:'tbd',away_team_id:'tbd'}]).map((m,i) => (
                              <div key={i} className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                                <div className="px-3 py-1 text-[9px] text-gray-500 uppercase border-b border-white/5">S{i+1}: Q{i+1} v Q{i+3}</div>
                                <div className="p-2 space-y-1">
                                  <TeamSlot id={m.home_team_id} score={m.home_score} match={m} />
                                  <TeamSlot id={m.away_team_id} score={m.away_score} match={m} />
                                  <PenLabel match={m} />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center pt-16 shrink-0"><ChevronRight size={20} className="text-emerald-500" /></div>
                          <div className="flex flex-col gap-4 w-[200px] shrink-0 justify-center pt-12">
                            <h4 className="text-yellow-500 text-xs font-bold uppercase text-center">Final</h4>
                            <div className="bg-[#18181b] border border-yellow-500/30 rounded-lg overflow-hidden">
                              <div className="px-3 py-1 text-[9px] text-yellow-500 uppercase border-b border-yellow-500/20 bg-yellow-500/5">Grand Final</div>
                              <div className="p-2 space-y-1">
                                <TeamSlot id={fin?.home_team_id} score={fin?.home_score} match={fin} />
                                <TeamSlot id={fin?.away_team_id} score={fin?.away_score} match={fin} />
                                <PenLabel match={fin} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="text-center text-gray-500 py-12">No standings data yet</div>
            )}
          </TabsContent>

          {/* MATCHES */}
          <TabsContent value="matches">
            {/* Calendar Sync */}
            <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-[#18181b] border border-white/10 rounded-lg">
              <CalendarPlus className="text-emerald-400 shrink-0" size={20} />
              <span className="text-white text-sm font-medium">Sync to Calendar</span>
              <a
                href={`${API_URL}/api/calendar/${organization.org_id}.ics`}
                download
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 rounded text-xs transition-colors"
                data-testid="download-full-calendar"
              >
                <Download size={12} /> Full Schedule (.ics)
              </a>
              <select
                onChange={(e) => {
                  if (e.target.value) window.location.href = `${API_URL}/api/calendar/${organization.org_id}.ics?team_id=${e.target.value}`;
                }}
                className="bg-[#0f0f10] border border-white/10 text-gray-300 text-xs rounded px-2 py-1.5"
                defaultValue=""
                data-testid="team-calendar-select"
              >
                <option value="" disabled>Filter by team...</option>
                {(teams || []).map(t => (
                  <option key={t.team_id} value={t.team_id}>{t.name}</option>
                ))}
              </select>
            </div>

            {stageOrder.map(stage => {
              const stageMatches = groupedMatches[stage];
              if (!stageMatches?.length) return null;
              
              const qfLabels = { 0: 'Q1', 1: 'Q2', 2: 'Q3', 3: 'Q4' };
              const qfDescs = { 0: 'A1 v C2', 1: 'A2 v C1', 2: 'B1 v D2', 3: 'B2 v D1' };
              const sfLabels = { 0: 'S1', 1: 'S2' };
              const sfDescs = { 0: 'Q1 v Q3', 1: 'Q2 v Q4' };
              
              return (
                <div key={stage} className="mb-8">
                  <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">{stageLabels[stage] || stage}</h3>
                  <div className="space-y-2">
                    {stageMatches.map((match, matchIdx) => {
                      const homeTd = teamMap[match.home_team_id];
                      const awayTd = teamMap[match.away_team_id];
                      const homeLogo = homeTd?.logo_data || homeTd?.logo_url || homeTd?.logo;
                      const awayLogo = awayTd?.logo_data || awayTd?.logo_url || awayTd?.logo;
                      
                      let matchLabel = null;
                      let matchDesc = null;
                      if (stage === 'quarterfinal') {
                        matchLabel = qfLabels[matchIdx];
                        matchDesc = qfDescs[matchIdx];
                      } else if (stage === 'semifinal') {
                        matchLabel = sfLabels[matchIdx];
                        matchDesc = sfDescs[matchIdx];
                      } else if (stage === 'final') {
                        matchLabel = 'F';
                        matchDesc = 'S1 v S2';
                      }
                      
                      return (
                      <Card key={match.match_id} className="bg-[#18181b] border-white/10 cursor-pointer hover:border-emerald-500/30 transition-all" onClick={() => setSelectedMatch(match)}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {matchLabel && (
                                <div className="text-center min-w-[32px]">
                                  <span className="text-emerald-400 font-bold text-xs">{matchLabel}</span>
                                  {matchDesc && <p className="text-gray-600 text-[9px] leading-tight">{matchDesc}</p>}
                                </div>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                                match.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 
                                match.status === 'playing' ? 'bg-green-500/20 text-green-400 animate-pulse' :
                                match.status === 'delayed' ? 'bg-red-500/20 text-red-400' :
                                'bg-white/5 text-gray-400'
                              }`}>{match.status === 'playing' ? 'LIVE' : match.status}</span>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                  {homeLogo ? <img src={homeLogo} alt="" className="w-6 h-6 object-contain rounded" /> : null}
                                  <span className="text-white font-medium text-sm">{match.home_team_name || 'TBD'}{forfeitTeams.has(match.home_team_id) ? '*' : ''}</span>
                                </div>
                                {(match.status === 'completed' || match.status === 'playing') ? (
                                  <div className="text-center px-3">
                                    <span className={`font-mono text-lg font-bold ${match.status === 'playing' ? 'text-green-400' : 'text-white'}`}>{match.home_score} - {match.away_score}{match.forfeit ? '*' : ''}</span>
                                    {match.penalties && <div className="text-amber-400 text-[8px] font-bold">(Pen: {match.home_pen_score}-{match.away_pen_score})</div>}
                                  </div>
                                ) : (
                                  <span className="text-gray-500 px-3">vs</span>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <span className="text-white font-medium text-sm">{match.away_team_name || 'TBD'}{forfeitTeams.has(match.away_team_id) ? '*' : ''}</span>
                                  {awayLogo ? <img src={awayLogo} alt="" className="w-6 h-6 object-contain rounded" /> : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              {match.forfeit && (
                                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded">*{match.forfeit}</span>
                              )}
                              {match.status === 'delayed' && match.delay_reason && (
                                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded">{match.delay_reason}</span>
                              )}
                              {match.field && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded">{match.field}</span>}
                              <span className="font-mono">{match.match_date} {match.match_time} MT</span>
                              {match.match_date && (
                                <a
                                  href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent((match.home_team_name||'TBD')+' vs '+(match.away_team_name||'TBD'))}&dates=${(match.match_date||'').replace(/-/g,'')}T${(match.match_time||'00:00').replace(':','')}00/${(match.match_date||'').replace(/-/g,'')}T${String(parseInt((match.match_time||'00:00').split(':')[0])+1).padStart(2,'0')}${(match.match_time||'00:00').split(':')[1]}00&location=${encodeURIComponent(match.field||'')}&details=${encodeURIComponent((stageLabels[match.stage]||match.stage))}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-emerald-400 hover:text-emerald-300"
                                  title="Add to Google Calendar"
                                >
                                  <CalendarPlus size={12} />
                                </a>
                              )}
                            </div>
                          </div>
                          {match.match_events?.length > 0 && (() => {
                            const homeEvents = match.match_events.filter(e => e.team_id === match.home_team_id);
                            const awayEvents = match.match_events.filter(e => e.team_id === match.away_team_id);
                            const neutralEvents = match.match_events.filter(e => e.team_id !== match.home_team_id && e.team_id !== match.away_team_id);
                            
                            const renderEvent = (evt, i, align) => {
                              const isRight = align === 'right';
                              const isGeneric = !evt.player_id && (!evt.player_name || evt.player_name === 'Goal');
                              const displayName = isGeneric ? 'Goal' : (evt.player_name || 'Unknown');
                              if (evt.event_type === 'goal') {
                                return (
                                  <div key={`g-${i}`} className={`text-xs flex items-center gap-1 ${isRight ? 'flex-row-reverse text-right' : ''}`}>
                                    <span>⚽</span>
                                    <span className={`font-medium ${isGeneric ? 'text-gray-500 italic' : 'text-white'}`}>{displayName}</span>
                                    {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                                    {evt.assist_by && <span className="text-blue-400">(A: {evt.assist_by})</span>}
                                  </div>
                                );
                              } else if (evt.event_type === 'yellow_card') {
                                return (
                                  <div key={`y-${i}`} className={`text-xs flex items-center gap-1 ${isRight ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className="w-2 h-3 bg-yellow-400 rounded-sm" />
                                    <span className="text-gray-300">{displayName}</span>
                                    {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                                  </div>
                                );
                              } else if (evt.event_type === 'red_card') {
                                return (
                                  <div key={`r-${i}`} className={`text-xs flex items-center gap-1 ${isRight ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className="w-2 h-3 bg-red-500 rounded-sm" />
                                    <span className="text-gray-300">{displayName}</span>
                                    {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                                  </div>
                                );
                              } else if (evt.event_type === 'assist') {
                                return (
                                  <div key={`a-${i}`} className={`text-xs flex items-center gap-1 ${isRight ? 'flex-row-reverse text-right' : ''}`}>
                                    <span className="font-bold text-blue-400">A</span>
                                    <span className="text-blue-300">{displayName}</span>
                                    {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                                  </div>
                                );
                              }
                              return null;
                            };
                            
                            return (
                              <div className="mt-2 pt-2 border-t border-white/5">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-0.5">
                                    {homeEvents.map((evt, i) => renderEvent(evt, i, 'left'))}
                                  </div>
                                  <div className="space-y-0.5">
                                    {awayEvents.map((evt, i) => renderEvent(evt, i, 'right'))}
                                  </div>
                                </div>
                                {neutralEvents.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {neutralEvents.map((evt, i) => renderEvent(evt, i, 'left'))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* TEAMS */}
          <TabsContent value="teams">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(teams || []).map(team => (
                <Card key={team.team_id} className="bg-[#18181b] border-white/10 cursor-pointer hover:border-emerald-500/30 transition-all" onClick={() => setSelectedTeam(team)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      {team.logo_url || team.logo_data ? (
                        <img src={team.logo_data || team.logo_url} alt="" className="w-12 h-12 object-contain rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-emerald-500/10 rounded flex items-center justify-center">
                          <span className="text-emerald-500 font-bold text-lg">{team.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h4 className="text-white font-medium">{team.name}</h4>
                        {team.pool && <span className="text-xs text-emerald-400">Pool {team.pool}</span>}
                      </div>
                    </div>
                    {team.bio && <p className="text-gray-400 text-xs mb-2">{team.bio}</p>}
                    <div className="flex gap-4 text-xs text-gray-500">
                      {team.years_in_competition && <span>Years: {team.years_in_competition}</span>}
                      {team.best_record && <span>Best: {team.best_record}</span>}
                    </div>
                    <div className="mt-2 pt-2 border-t border-white/5 text-xs text-gray-400">
                      {getTeamPlayers(team.team_id).length} players | P{team.played} W{team.won} D{team.drawn} L{team.lost} | {team.points} pts
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* PLAYERS */}
          {/* PLAYER STATS */}
          <TabsContent value="stats">
            <div className="flex justify-end mb-4">
              <a href={`${API_URL}/api/export/players/${organization.org_id}`} download className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 rounded text-sm transition-colors" data-testid="export-players-csv">
                <Users className="w-4 h-4" /> Export CSV
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><Target size={16} className="text-emerald-500" />Top Scorers</h3></div>
                {topScorers.filter(p => p.goals > 0).length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No goals scored yet</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {topScorers.filter(p => p.goals > 0).map((p, i) => (
                      <div key={p.player_id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-orange-600 text-white' : 'bg-white/5 text-gray-500'}`}>{i+1}</span>
                          <div><span className="text-white text-sm">{p.name}</span><span className="text-gray-500 text-xs ml-2">{p.team_name}</span></div>
                        </div>
                        <span className="text-emerald-500 font-bold text-lg">{p.goals}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><Users size={16} className="text-blue-500" />Top Assists</h3></div>
                {topAssists.filter(p => p.assists > 0).length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No assists recorded yet</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {topAssists.filter(p => p.assists > 0).map((p, i) => (
                      <div key={p.player_id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-orange-600 text-white' : 'bg-white/5 text-gray-500'}`}>{i+1}</span>
                          <div><span className="text-white text-sm">{p.name}</span><span className="text-gray-500 text-xs ml-2">{p.team_name}</span></div>
                        </div>
                        <span className="text-blue-500 font-bold text-lg">{p.assists}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><div className="w-3 h-4 bg-yellow-400 rounded-sm" />Yellow Cards</h3></div>
                {playersWithTeams.filter(p => p.yellow_cards > 0).length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No yellow cards yet</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {playersWithTeams.filter(p => p.yellow_cards > 0).sort((a,b) => b.yellow_cards - a.yellow_cards).map((p, i) => (
                      <div key={p.player_id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">{i+1}</span>
                          <div><span className="text-white text-sm">{p.name}</span><span className="text-gray-500 text-xs ml-2">{p.team_name}</span></div>
                        </div>
                        <span className="text-yellow-400 font-bold text-lg">{p.yellow_cards}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><div className="w-3 h-4 bg-red-500 rounded-sm" />Red Cards</h3></div>
                {playersWithTeams.filter(p => p.red_cards > 0).length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No red cards yet</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {playersWithTeams.filter(p => p.red_cards > 0).sort((a,b) => b.red_cards - a.red_cards).map((p, i) => (
                      <div key={p.player_id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">{i+1}</span>
                          <div><span className="text-white text-sm">{p.name}</span><span className="text-gray-500 text-xs ml-2">{p.team_name}</span></div>
                        </div>
                        <span className="text-red-500 font-bold text-lg">{p.red_cards}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><div className="w-3 h-4 bg-yellow-400 rounded-sm border-2 border-orange-500" />Admin Yellow Cards</h3></div>
                {(!admin_card_events || admin_card_events.filter(ac => ac.card_color === 'yellow').length === 0) ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No admin yellow cards</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {admin_card_events.filter(ac => ac.card_color === 'yellow').map((ac, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-4 bg-yellow-400 rounded-sm border-2 border-orange-500 shrink-0" />
                          <div>
                            <span className="text-white text-sm">{ac.player_name}</span>
                            <span className="text-gray-500 text-xs ml-2">{ac.team_name}</span>
                            {ac.admin_note && <span className="text-orange-400 text-xs ml-2">({ac.admin_note})</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#18181b] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10"><h3 className="text-white font-heading uppercase text-sm flex items-center gap-2"><div className="w-3 h-4 bg-red-500 rounded-sm border-2 border-orange-500" />Admin Red Cards</h3></div>
                {(!admin_card_events || admin_card_events.filter(ac => ac.card_color === 'red').length === 0) ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No admin red cards</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {admin_card_events.filter(ac => ac.card_color === 'red').map((ac, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-4 bg-red-500 rounded-sm border-2 border-orange-500 shrink-0" />
                          <div>
                            <span className="text-white text-sm">{ac.player_name}</span>
                            <span className="text-gray-500 text-xs ml-2">{ac.team_name}</span>
                            {ac.admin_note && <span className="text-orange-400 text-xs ml-2">({ac.admin_note})</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Export Cards Summary */}
            <div className="mt-4 flex justify-center">
              <a
                href={`${API_URL}/api/export/cards-summary/${organization.org_id}`}
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20 rounded-lg text-sm transition-colors"
                data-testid="export-cards-summary"
                onClick={(e) => {
                  const url = `${API_URL}/api/export/cards-summary/${organization.org_id}`;
                  navigator.clipboard.writeText(url).catch(() => {});
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export Cards Summary (Excel)
              </a>
            </div>
          </TabsContent>
          <TabsContent value="sponsors">
            {(!sponsors || sponsors.length === 0) ? (
              <div className="text-center text-gray-500 py-12">No sponsors yet</div>
            ) : (
              <div className="space-y-8">
                {['gold','silver','bronze'].map(tier => {
                  const tierSponsors = sponsorsByTier[tier];
                  if (!tierSponsors?.length) return null;
                  return (
                    <div key={tier}>
                      <h3 className="font-heading text-lg font-bold text-white uppercase mb-3 flex items-center gap-2">
                        <Award className={tier === 'gold' ? 'text-yellow-500' : tier === 'silver' ? 'text-gray-400' : 'text-orange-600'} size={20} />
                        {tier} Sponsors
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {tierSponsors.map(s => (
                          <Card key={s.sponsor_id} className="bg-[#18181b] border-white/10">
                            <CardContent className="p-6">
                              <div className="flex items-center gap-4">
                                {(s.logo_data || s.logo_url) && <img src={s.logo_data || s.logo_url} alt={s.name} className="h-20 w-20 object-contain rounded" />}
                                <div>
                                  <p className="text-white font-bold text-lg">{s.name}</p>
                                  {s.message && <p className="text-gray-400 text-sm mt-1">{s.message}</p>}
                                  {s.website_url && <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-emerald-500 text-xs hover:underline mt-1 inline-block">Visit website</a>}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* TOURNAMENT INFO */}
          <TabsContent value="info">
            {tournament_info ? (
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-6 space-y-6">
                  {tournament_info.tournament_description && (
                    <div>
                      <h3 className="text-white font-medium mb-2">About the Tournament</h3>
                      <p className="text-gray-400 text-sm whitespace-pre-wrap">{tournament_info.tournament_description}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {tournament_info.event_date && (
                      <div><h4 className="text-gray-300 text-sm font-medium mb-1">Date</h4><p className="text-gray-400 text-sm">{tournament_info.event_date}</p></div>
                    )}
                    {tournament_info.event_location && (
                      <div><h4 className="text-gray-300 text-sm font-medium mb-1">Location</h4><p className="text-gray-400 text-sm flex items-center gap-1"><MapPin size={14} className="text-emerald-500" />{tournament_info.event_location}</p></div>
                    )}
                    {tournament_info.contact_info && (
                      <div><h4 className="text-gray-300 text-sm font-medium mb-1">Contact</h4><p className="text-gray-400 text-sm">{tournament_info.contact_info}</p></div>
                    )}
                  </div>
                  {tournament_info.rules && (
                    <div><h3 className="text-white font-medium mb-2">Rules & Format</h3><p className="text-gray-400 text-sm whitespace-pre-wrap">{tournament_info.rules}</p></div>
                  )}
                  {tournament_info.additional_notes && (
                    <div><h3 className="text-white font-medium mb-2">Additional Notes</h3><p className="text-gray-400 text-sm whitespace-pre-wrap">{tournament_info.additional_notes}</p></div>
                  )}
                  {/* Tournament Images */}
                  {tournament_info.images?.length > 0 && (
                    <div>
                      <h3 className="text-white font-medium mb-3">Gallery</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tournament_info.images.map((img, i) => (
                          <img key={i} src={img} alt={`Tournament ${i+1}`} className="w-full h-48 object-cover rounded-lg border border-white/10" />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Host Team Section */}
                  {tournament_info.host_team_image && (
                    <div className="border-t border-white/10 pt-6">
                      <h3 className="text-white font-medium mb-3">About the Host</h3>
                      <div className="flex flex-col md:flex-row items-start gap-4">
                        <img src={tournament_info.host_team_image} alt="Host" className="w-full md:w-48 h-48 object-cover rounded-lg border border-white/10" />
                        {tournament_info.host_team_description && (
                          <p className="text-gray-400 text-sm whitespace-pre-wrap">{tournament_info.host_team_description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="text-center text-gray-500 py-12">Tournament information coming soon</div>
            )}
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog">
            {blogPosts.length === 0 ? (
              <div className="text-center text-gray-500 py-12">No blog posts yet. Check back soon!</div>
            ) : (
              <div className="space-y-6">
                {blogPosts.map(post => (
                  <Card key={post.post_id} className="bg-[#18181b] border-white/10">
                    <CardContent className="p-6">
                      {post.image && <img src={post.image} alt="" className="w-full h-48 object-cover rounded-lg mb-4" />}
                      <h3 className="text-white text-xl font-bold mb-2">{post.title}</h3>
                      <p className="text-gray-500 text-xs mb-3">{new Date(post.created_at).toLocaleDateString()} by {post.author_name}</p>
                      <p className="text-gray-400 text-sm whitespace-pre-wrap mb-4"><Linkify>{post.content}</Linkify></p>
                      
                      <button
                        onClick={() => { setSelectedPost(selectedPost === post.post_id ? null : post.post_id); if (selectedPost !== post.post_id) fetchComments(post.post_id); }}
                        className="text-emerald-500 text-sm hover:text-emerald-400 flex items-center gap-1"
                      >
                        {post.comment_count || 0} Comments {selectedPost === post.post_id ? '▲' : '▼'}
                      </button>
                      
                      {selectedPost === post.post_id && (
                        <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                          {postComments.map(c => (
                            <div key={c.comment_id} className="bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-white text-sm font-medium">{c.author_name}</span>
                                <span className="text-gray-500 text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
                              </div>
                              <p className="text-gray-400 text-sm">{c.content}</p>
                            </div>
                          ))}
                          <div className="flex gap-2 mt-3">
                            <input value={newComment.author_name} onChange={(e) => setNewComment({...newComment, author_name: e.target.value})} placeholder="Your name" className="flex-none w-32 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm" />
                            <input value={newComment.content} onChange={(e) => setNewComment({...newComment, content: e.target.value})} placeholder="Write a comment..." className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm" />
                            <button onClick={() => handleComment(post.post_id)} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-sm">Post</button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            {gallerySegments.length === 0 ? (
              <div className="text-center text-gray-500 py-12">No gallery photos yet. Check back soon!</div>
            ) : (
              <div className="space-y-8">
                {gallerySegments.map((seg) => (
                  <div key={seg.segment_id}>
                    <h3 className="text-white font-bold text-lg mb-3">{seg.name}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {(seg.media || []).map((m) => (
                        <div key={m.media_id} className="rounded-lg overflow-hidden bg-black aspect-square">
                          {m.content_type?.startsWith('video/') ? (
                            <video src={`${API_URL}/api/gallery/media/${m.storage_path}`} className="w-full h-full object-cover" controls preload="metadata" />
                          ) : (
                            <img src={`${API_URL}/api/gallery/media/${m.storage_path}`} alt={m.original_filename} className="w-full h-full object-cover" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SUBSCRIBE / NOTIFICATIONS */}
          <TabsContent value="subscribe">
            <Card className="bg-[#18181b] border-white/10 max-w-lg mx-auto">
              <CardContent className="p-6 space-y-5">
                <div className="text-center mb-4">
                  <h3 className="text-white text-xl font-bold mb-1">Stay Updated</h3>
                  <p className="text-gray-400 text-sm">Get notified about match schedules, score updates, and more</p>
                </div>
                <div className="space-y-3">
                  <input value={subForm.email} onChange={(e) => setSubForm({...subForm, email: e.target.value})} type="email" placeholder="Your email address" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-500" />
                  <input value={subForm.phone} onChange={(e) => setSubForm({...subForm, phone: e.target.value})} type="tel" placeholder="Phone number (optional, for SMS)" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-500" />
                </div>
                <div className="space-y-2">
                  <p className="text-gray-300 text-sm font-medium">Notify me about:</p>
                  {[
                    ['notify_match_schedule', 'Match schedule & kick-off times'],
                    ['notify_match_changes', 'Score updates & match results'],
                    ['notify_referee_assigned', 'Referee assignments'],
                    ['notify_field_time_change', 'Field or time changes'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 text-sm text-gray-400 cursor-pointer">
                      <input type="checkbox" checked={subPrefs[key]} onChange={(e) => setSubPrefs({...subPrefs, [key]: e.target.checked})} className="rounded border-white/20 bg-white/5 text-emerald-500" />
                      {label}
                    </label>
                  ))}
                </div>
                {teams && teams.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-gray-300 text-sm font-medium">Follow specific teams (optional):</p>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {teams.map(t => (
                        <label key={t.team_id} className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={subForm.follow_teams.includes(t.team_id)}
                            onChange={(e) => {
                              const ft = e.target.checked
                                ? [...subForm.follow_teams, t.team_id]
                                : subForm.follow_teams.filter(id => id !== t.team_id);
                              setSubForm({...subForm, follow_teams: ft});
                            }}
                            className="rounded border-white/20 bg-white/5 text-emerald-500"
                          />
                          {t.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={handleSubscribe}
                  disabled={subscribing || (!subForm.email && !subForm.phone)}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 text-white rounded-lg font-medium text-sm transition-all"
                >
                  {subscribing ? 'Subscribing...' : 'Subscribe to Updates'}
                </button>
                <p className="text-gray-500 text-xs text-center">You can unsubscribe at any time</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Match Detail Dialog */}
      <Dialog open={!!selectedMatch} onOpenChange={() => setSelectedMatch(null)}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Match Details</DialogTitle></DialogHeader>
          {selectedMatch && (
            <div className="space-y-4 mt-2">
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center"><p className="text-gray-400 text-sm mb-1">{selectedMatch.home_team_name || 'TBD'}</p><p className="text-3xl font-bold text-white">{selectedMatch.home_score}</p></div>
                  <span className="text-gray-500 text-xl">-</span>
                  <div className="text-center"><p className="text-gray-400 text-sm mb-1">{selectedMatch.away_team_name || 'TBD'}</p><p className="text-3xl font-bold text-white">{selectedMatch.away_score}</p></div>
                </div>
                <p className="text-gray-500 text-xs mt-2">{selectedMatch.match_date} {selectedMatch.match_time} MT{selectedMatch.field && <span className="ml-2 text-blue-400">{selectedMatch.field}</span>}</p>
              </div>
              {selectedMatch.match_events?.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <h4 className="text-gray-300 text-sm font-medium mb-2">Match Events</h4>
                  <div className="space-y-1.5">
                    {selectedMatch.match_events.filter(e => e.event_type === 'goal').map((evt, i) => (
                      <div key={`g-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 font-mono w-10 text-right">{evt.minute}&apos;</span>
                        <span>⚽</span>
                        <span className="text-white font-medium">{evt.player_name}</span>
                        {evt.assist_by && <span className="text-blue-400 text-xs ml-1">(A: {evt.assist_by})</span>}
                      </div>
                    ))}
                    {selectedMatch.match_events.filter(e => e.event_type === 'assist').map((evt, i) => (
                      <div key={`a-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 font-mono w-10 text-right">{evt.minute}&apos;</span>
                        <span className="font-bold text-blue-400">A</span>
                        <span className="text-blue-300">{evt.player_name}</span>
                      </div>
                    ))}
                    {selectedMatch.match_events.filter(e => e.event_type === 'yellow_card').map((evt, i) => (
                      <div key={`y-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 font-mono w-10 text-right">{evt.minute}&apos;</span>
                        <div className="w-3 h-4 bg-yellow-400 rounded-sm" />
                        <span className="text-gray-300">{evt.player_name}</span>
                      </div>
                    ))}
                    {selectedMatch.match_events.filter(e => e.event_type === 'red_card').map((evt, i) => (
                      <div key={`r-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 font-mono w-10 text-right">{evt.minute}&apos;</span>
                        <div className="w-3 h-4 bg-red-500 rounded-sm" />
                        <span className="text-gray-300">{evt.player_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-3">
                <h4 className="text-gray-300 text-sm font-medium mb-2 flex items-center gap-1"><Shield size={14} className="text-emerald-500" /> Match Officials</h4>
                <div className="grid grid-cols-1 gap-1.5 text-sm">
                  {[['Center Ref', selectedMatch.main_referee_name],['1st Assist', selectedMatch.assistant_referee_1_name],['2nd Assist', selectedMatch.assistant_referee_2_name],['4th Official', selectedMatch.fourth_official_name],['Delegate', selectedMatch.match_delegate_name]].map(([role, name]) => (
                    <div key={role} className="flex items-center justify-between"><span className="text-gray-500">{role}</span><span className={name && name !== 'Unassigned' ? 'text-white' : 'text-gray-600'}>{name || 'Unassigned'}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Detail Dialog */}
      <Dialog open={!!selectedTeam} onOpenChange={() => setSelectedTeam(null)}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">{selectedTeam?.name}</DialogTitle></DialogHeader>
          {selectedTeam && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-4">
                {(selectedTeam.logo_data || selectedTeam.logo_url) ? (
                  <img src={selectedTeam.logo_data || selectedTeam.logo_url} alt="" className="w-16 h-16 object-contain rounded" />
                ) : (
                  <div className="w-16 h-16 bg-emerald-500/10 rounded flex items-center justify-center"><span className="text-emerald-500 font-bold text-2xl">{selectedTeam.name.charAt(0)}</span></div>
                )}
                <div>
                  {selectedTeam.pool && <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Pool {selectedTeam.pool}</span>}
                  {selectedTeam.bio && <p className="text-gray-400 text-sm mt-1">{selectedTeam.bio}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedTeam.years_in_competition && <div><span className="text-gray-500">Years</span><p className="text-white">{selectedTeam.years_in_competition}</p></div>}
                {selectedTeam.best_record && <div><span className="text-gray-500">Best Record</span><p className="text-white">{selectedTeam.best_record}</p></div>}
                <div><span className="text-gray-500">Record</span><p className="text-white">P{selectedTeam.played} W{selectedTeam.won} D{selectedTeam.drawn} L{selectedTeam.lost}</p></div>
                <div><span className="text-gray-500">Points</span><p className="text-white font-bold">{selectedTeam.points}</p></div>
              </div>
              <div className="border-t border-white/10 pt-3">
                <h4 className="text-gray-300 text-sm font-medium mb-2">Squad ({getTeamPlayers(selectedTeam.team_id).length})</h4>
                {getTeamPlayers(selectedTeam.team_id).length === 0 ? <p className="text-gray-500 text-sm">No players registered</p> : (
                  <div className="space-y-1">
                    {getTeamPlayers(selectedTeam.team_id).map(p => (
                      <div key={p.player_id} className="flex items-center justify-between text-sm py-1 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-2"><span className="text-emerald-500 font-mono w-6 text-right">#{p.number}</span><span className="text-white">{p.name}</span></div>
                        <span className="text-gray-400 text-xs">{p.position}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/cnvt-logo.jpg" alt="CNVT" className="w-10 h-10 rounded object-contain" />
            <span className="text-gray-500 text-sm">|</span>
            <img src="/kilimanjaro-logo.png" alt="Kilimanjaro" className="h-10 object-contain" />
            <span className="text-gray-500 text-sm">|</span>
            <img src="/africremit-logo.jpg" alt="Afric Remit" className="h-10 object-contain rounded" />
          </div>
          <p className="text-gray-500 text-sm mb-3">
            Powered by <Link to="/" className="text-emerald-500 hover:text-emerald-400 font-medium">Score Copilot</Link>
          </p>
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 px-6 py-2.5 rounded-full text-sm font-medium transition-all"
          >
            Want to run your own league? Start free with ScoreCopilot <ChevronRight size={16} />
          </Link>
        </div>
      </footer>
    </div>
  );
}
